import React from 'react';
import clsx from 'clsx';
import { FiCheck, FiAlertTriangle, FiXCircle, FiTool } from 'react-icons/fi';
import { SystemStatus } from '../../types';

interface StatusBadgeProps {
  status: SystemStatus;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  pulse?: boolean;
}

const statusConfig: Record<SystemStatus, {
  label: string;
  bgColor: string;
  textColor: string;
  dotColor: string;
  icon: React.ReactNode;
}> = {
  operational: {
    label: 'All Systems Operational',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    dotColor: 'bg-green-500',
    icon: <FiCheck className="w-4 h-4" />,
  },
  degraded: {
    label: 'Degraded Performance',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-800',
    dotColor: 'bg-amber-500',
    icon: <FiAlertTriangle className="w-4 h-4" />,
  },
  outage: {
    label: 'Service Outage',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    dotColor: 'bg-red-500',
    icon: <FiXCircle className="w-4 h-4" />,
  },
  maintenance: {
    label: 'Under Maintenance',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    dotColor: 'bg-blue-500',
    icon: <FiTool className="w-4 h-4" />,
  },
};

const sizeClasses = {
  sm: 'px-2 py-1 text-xs',
  md: 'px-3 py-1.5 text-sm',
  lg: 'px-4 py-2 text-base',
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  size = 'md',
  showLabel = true,
  pulse = true,
}) => {
  const config = statusConfig[status];

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-2 rounded-full font-medium',
        config.bgColor,
        config.textColor,
        sizeClasses[size]
      )}
    >
      <span className="relative flex h-2 w-2">
        {pulse && status !== 'operational' && (
          <span
            className={clsx(
              'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
              config.dotColor
            )}
          />
        )}
        <span
          className={clsx(
            'relative inline-flex rounded-full h-2 w-2',
            config.dotColor
          )}
        />
      </span>
      {showLabel && <span>{config.label}</span>}
    </span>
  );
};

export default StatusBadge;
